import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';


const AccountSecurityTab = ({ onSave }) => {
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [errors, setErrors] = useState({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [activeSessions] = useState([
    {
      id: 1,
      device: 'Chrome on Windows',
      location: 'Mumbai, India',
      lastActive: '2 minutes ago',
      isCurrent: true
    },
    {
      id: 2,
      device: 'Safari on iPhone',
      location: 'Mumbai, India',
      lastActive: '2 hours ago',
      isCurrent: false
    },
    {
      id: 3,
      device: 'Chrome on Android',
      location: 'Delhi, India',
      lastActive: '1 day ago',
      isCurrent: false
    }
  ]);

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordData?.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!passwordData?.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordData?.newPassword?.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/?.test(passwordData?.newPassword)) {
      newErrors.newPassword = 'Password must contain uppercase, lowercase, and number';
    }

    if (!passwordData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (passwordData?.newPassword !== passwordData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmitPasswordChange = () => {
    if (!validatePasswordForm()) return;

    setIsChangingPassword(true);
    setTimeout(() => {
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setIsChangingPassword(false);
      alert('Password changed successfully!');
    }, 1500);
  };

  const handleEnableTwoFactor = () => {
    setShowTwoFactorSetup(true);
  };

  const handleVerifyTwoFactor = () => {
    if (verificationCode === '123456') {
      setTwoFactorEnabled(true);
      setShowTwoFactorSetup(false);
      setVerificationCode('');
      alert('Two-factor authentication enabled successfully!');
    } else {
      alert('Invalid verification code. Use: 123456');
    }
  };

  const handleDisableTwoFactor = () => {
    setTwoFactorEnabled(false);
    alert('Two-factor authentication disabled');
  };

  const handleTerminateSession = (sessionId) => {
    alert(`Session ${sessionId} terminated`);
  };

  const handleDeleteAccount = () => {
    alert('Account deletion requested. This action cannot be undone.');
    setShowDeleteConfirm(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 p-4 bg-warning/10 rounded-lg border border-warning/20">
        <Icon name="AlertTriangle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-sm font-caption font-semibold text-warning mb-1">Security Notice</h3>
          <p className="text-xs text-muted-foreground">
            Keep your account secure by using a strong password and enabling two-factor authentication.
          </p>
        </div>
      </div>
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-heading font-semibold mb-4">Change Password</h3>
          <div className="space-y-4">
            <Input
              label="Current Password"
              type="password"
              placeholder="Enter current password"
              value={passwordData?.currentPassword}
              onChange={(e) => handlePasswordChange('currentPassword', e?.target?.value)}
              error={errors?.currentPassword}
              required
            />

            <Input
              label="New Password"
              type="password"
              placeholder="Enter new password"
              value={passwordData?.newPassword}
              onChange={(e) => handlePasswordChange('newPassword', e?.target?.value)}
              error={errors?.newPassword}
              required
              description="Must be at least 8 characters with uppercase, lowercase, and number"
            />

            <Input
              label="Confirm New Password"
              type="password"
              placeholder="Confirm new password"
              value={passwordData?.confirmPassword}
              onChange={(e) => handlePasswordChange('confirmPassword', e?.target?.value)}
              error={errors?.confirmPassword}
              required
            />

            <Button
              variant="default"
              fullWidth
              loading={isChangingPassword}
              iconName="Lock"
              iconPosition="left"
              onClick={handleSubmitPasswordChange}
            >
              Change Password
            </Button>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-heading font-semibold mb-4">Two-Factor Authentication</h3>
          <div className="flex items-start gap-4 p-4 bg-muted rounded-lg">
            <div className={`p-2 rounded-lg ${twoFactorEnabled ? 'bg-success/10' : 'bg-muted-foreground/10'}`}>
              <Icon 
                name={twoFactorEnabled ? 'ShieldCheck' : 'Shield'} 
                size={24} 
                color={twoFactorEnabled ? 'var(--color-success)' : 'var(--color-muted-foreground)'} 
              />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-caption font-semibold mb-1">
                {twoFactorEnabled ? 'Enabled' : 'Disabled'}
              </h4>
              <p className="text-xs text-muted-foreground mb-3">
                {twoFactorEnabled 
                  ? 'Your account is protected with two-factor authentication' :'Add an extra layer of security to your account'}
              </p>
              <Button
                variant={twoFactorEnabled ? 'destructive' : 'default'}
                size="sm"
                iconName={twoFactorEnabled ? 'ShieldOff' : 'ShieldCheck'}
                iconPosition="left"
                onClick={twoFactorEnabled ? handleDisableTwoFactor : handleEnableTwoFactor}
              >
                {twoFactorEnabled ? 'Disable' : 'Enable'} 2FA
              </Button>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-heading font-semibold mb-4">Active Sessions</h3>
          <div className="space-y-3">
            {activeSessions?.map((session) => (
              <div
                key={session?.id}
                className="flex items-start gap-3 p-4 bg-muted rounded-lg"
              >
                <div className="p-2 bg-card rounded-lg">
                  <Icon name="Monitor" size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm font-caption font-semibold">{session?.device}</h4>
                    {session?.isCurrent && (
                      <span className="px-2 py-0.5 bg-success/10 text-success rounded-full text-xs font-medium">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mb-1">{session?.location}</p>
                  <p className="text-xs text-muted-foreground">Last active: {session?.lastActive}</p>
                </div>
                {!session?.isCurrent && (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="X"
                    onClick={() => handleTerminateSession(session?.id)}
                  >
                    End
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-heading font-semibold mb-4 text-error">Danger Zone</h3>
          <div className="p-4 bg-error/10 rounded-lg border border-error/20">
            <div className="flex items-start gap-3 mb-4">
              <Icon name="AlertTriangle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-caption font-semibold text-error mb-1">Delete Account</h4>
                <p className="text-xs text-muted-foreground">
                  Once you delete your account, there is no going back. All your data will be permanently removed.
                </p>
              </div>
            </div>
            <Button
              variant="destructive"
              size="sm"
              iconName="Trash2"
              iconPosition="left"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete My Account
            </Button>
          </div>
        </div>
      </div>
      {showTwoFactorSetup && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-background/80">
          <div className="w-full max-w-md bg-card rounded-xl shadow-warm-2xl p-6 animate-scale-in">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="ShieldCheck" size={32} color="var(--color-primary)" />
              </div>
              <h3 className="text-lg font-heading font-semibold mb-2">Enable Two-Factor Authentication</h3>
              <p className="text-sm text-muted-foreground">
                Scan this QR code with your authenticator app or enter the code manually
              </p>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg text-center">
                <div className="w-48 h-48 mx-auto bg-card rounded-lg flex items-center justify-center mb-3">
                  <Icon name="QrCode" size={120} color="var(--color-muted-foreground)" />
                </div>
                <p className="text-xs font-mono text-muted-foreground">ABCD-EFGH-IJKL-MNOP</p>
              </div>

              <Input
                label="Verification Code"
                type="text"
                placeholder="Enter 6-digit code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e?.target?.value)}
                description="Mock code: 123456"
              />

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => {
                    setShowTwoFactorSetup(false);
                    setVerificationCode('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  fullWidth
                  iconName="Check"
                  iconPosition="left"
                  onClick={handleVerifyTwoFactor}
                  disabled={verificationCode?.length !== 6}
                >
                  Verify
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-background/80">
          <div className="w-full max-w-md bg-card rounded-xl shadow-warm-2xl p-6 animate-scale-in">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-error/10 flex items-center justify-center">
                <Icon name="AlertTriangle" size={32} color="var(--color-error)" />
              </div>
              <h3 className="text-lg font-heading font-semibold mb-2 text-error">Delete Account?</h3>
              <p className="text-sm text-muted-foreground">
                This action cannot be undone. All your data, messages, and profile information will be permanently deleted.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                fullWidth
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                fullWidth
                iconName="Trash2"
                iconPosition="left"
                onClick={handleDeleteAccount}
              >
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSecurityTab;